import React from "react";
import { useTranslation } from "react-i18next";
import {
  Stack,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
} from "@mui/material";
import SubActionButton from "@quest_editor/components/Buttons/SubActionButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";
import { Manufacturer } from "@quest_editor/mocks";
import SearchDialog from "./SearchDialog";

const ManufacturerField = ({
  manufacturerRows,
  setManufacturerRows,
  emptyLabel,
}: {
  manufacturerRows: Manufacturer[];
  setManufacturerRows: React.Dispatch<React.SetStateAction<Manufacturer[]>>;
  emptyLabel: string;
}) => {
  const { t } = useTranslation("questEditor");
  const [name, setName] = React.useState<string>("");
  const [open, setOpen] = React.useState(false);

  const handleRemoveManufacturer = (id: string) => {
    setManufacturerRows((prev: Manufacturer[]) =>
      prev.filter((row) => row.id !== id),
    );
  };

  const handleAddSearchDialog = (selected: Manufacturer[]) => {
    setManufacturerRows((prev: Manufacturer[]) => {
      const newItems = selected.filter(
        (s) => !prev.some((m) => m.name === s.name),
      );
      return [...prev, ...newItems];
    });
    setName("");
  };

  const handleOpenSearch = () => {
    setOpen(true);
  };

  return (
    <Accordion
      sx={{
        ".MuiAccordionSummary-root .MuiSvgIcon-root": {
          transform: "rotate(0deg)",
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {manufacturerRows.length > 0 ? (
          <Typography color="primary">
            {manufacturerRows.map((m) => m.name).join(", ")}
          </Typography>
        ) : (
          <Typography color="primary">{emptyLabel}</Typography>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="column" divider={<Divider />} spacing={1}>
          {manufacturerRows.map((row) => (
            <Stack
              key={row.id}
              direction="row"
              alignItems="center"
              spacing={4}
            >
              <Typography sx={{ color: "text.secondary", flexGrow: 1 }}>
                {row.name}
              </Typography>
              <IconButton onClick={() => handleRemoveManufacturer(row.id)}>
                <ClearIcon />
              </IconButton>
            </Stack>
          ))}
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              size="small"
              placeholder={t("common_field.manufacturer.placeholder")}
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
              sx={{ flexGrow: 1 }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") handleOpenSearch();
              }}
            />
            <SubActionButton
              onClick={handleOpenSearch}
              sx={{ whiteSpace: "nowrap" }}
            >
              {t("common_field.manufacturer.button")}
            </SubActionButton>

            <SearchDialog
              open={open}
              onClose={() => setOpen(false)}
              onAdd={handleAddSearchDialog}
              inputValue={name}
              existingRows={manufacturerRows}
            />
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default ManufacturerField;
